import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const briefings = [
  {
    title: "Antitrust Risks in Vertical Integration Transactions",
    summary: "Vertical mergers face increasing scrutiny as enforcement agencies expand theories of competitive harm beyond traditional horizontal analysis.",
    developments: "Updated Merger Guidelines now include enhanced vertical merger analysis. DOJ has challenged multiple vertical transactions citing foreclosure and raising rivals' costs theories.",
    implications: "Companies pursuing vertical acquisitions should expect heightened regulatory scrutiny, particularly in concentrated markets or where the merged firm could control critical inputs.",
    considerations: "Pre-transaction risk assessment should include analysis of vertical competitive effects. Early engagement with antitrust counsel recommended for vertical deals exceeding HSR thresholds.",
  },
  {
    title: "Competition Policy Developments Affecting Technology Markets",
    summary: "The technology sector remains the primary focus of antitrust enforcement, with multiple ongoing investigations and new legislative proposals targeting digital platforms.",
    developments: "FTC and DOJ have active investigations of major technology platforms. Proposed legislation would create new obligations for designated digital platforms.",
    implications: "Technology companies face an evolving regulatory landscape that may affect acquisition strategies, platform practices, and competitive conduct.",
    considerations: "Technology companies should review competitive practices proactively, assess acquisition strategies against heightened scrutiny standards, and develop robust compliance programs.",
  },
  {
    title: "Regulatory Scrutiny of Pharmaceutical Mergers",
    summary: "Pharmaceutical mergers face intense enforcement attention, with agencies focusing on innovation competition, pipeline overlaps, and effects on drug pricing.",
    developments: "FTC has challenged several pharmaceutical mergers citing harm to innovation competition and pipeline overlap concerns.",
    implications: "Pharmaceutical companies considering acquisitions should anticipate extended review timelines and potential challenges based on innovation competition theories.",
    considerations: "Deal teams should prepare detailed competitive analyses of pipeline overlaps and innovation competition effects early in the transaction planning process.",
  },
  {
    title: "Antitrust Considerations in Private Equity Roll-Up Strategies",
    summary: "Enforcement agencies have increased scrutiny of serial acquisitions by private equity firms, viewing roll-up strategies as a potential path to market concentration.",
    developments: "FTC has announced enhanced scrutiny of roll-up acquisitions and proposed changes to HSR reporting to capture serial acquisition patterns.",
    implications: "Private equity firms pursuing roll-up strategies may face aggregate competitive effects analysis across portfolio acquisitions.",
    considerations: "Investment committees should incorporate antitrust risk assessment into acquisition planning for portfolio companies operating in concentrated markets.",
  },
];

const StrategyBriefings = () => (
  <Layout>
    <PageHead title="Competition Strategy Briefings" description="Strategic antitrust briefings from Alicia J. Batts for corporate leadership, boards, investors, and general counsel on competition risk and regulatory trends." path="/strategy-briefings" keywords="Alicia Batts strategy briefings, antitrust risk briefing, competition strategy, board antitrust advisory, corporate competition counsel" />
    <section className="bg-hero text-primary-foreground py-20">
      <div className="editorial-container">
        <AnimatedSection animation="fade"><div className="gold-accent-line mb-6" /></AnimatedSection>
        <AnimatedSection animation="fade-up" delay={100}><h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Competition Strategy Briefings</h1></AnimatedSection>
        <AnimatedSection animation="fade-up" delay={200}><p className="text-lg opacity-80 max-w-2xl">Concise strategic insights for corporate leadership, boards, investors, and general counsel on critical competition policy developments.</p></AnimatedSection>
      </div>
    </section>

    <section className="section-padding">
      <div className="editorial-container space-y-12">
        {briefings.map((b, i) => (
          <AnimatedSection key={i} animation="fade-up" delay={i * 80}>
          <div className="bg-card rounded-lg p-8 md:p-10 shadow-card border border-border">
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-6">{b.title}</h2>
            <div className="space-y-6">
              {[
                { label: "Summary", content: b.summary },
                { label: "Key Enforcement Developments", content: b.developments },
                { label: "Potential Business Implications", content: b.implications },
                { label: "Strategic Considerations", content: b.considerations },
              ].map((section) => (
                <div key={section.label}>
                  <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-2">{section.label}</p>
                  <p className="text-muted-foreground leading-relaxed text-sm">{section.content}</p>
                </div>
              ))}
            </div>
          </div>
          </AnimatedSection>
        ))}
      </div>
    </section>

    <section className="py-16 bg-muted/50">
      <div className="editorial-container text-center">
        <h2 className="font-serif text-2xl font-semibold text-foreground mb-4">Request a Custom Briefing</h2>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">Batts Legal LLP prepares custom competition strategy briefings for corporate boards, executive leadership teams, and private equity investment committees.</p>
        <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-sm font-medium text-sm hover:opacity-90 transition-opacity">
          Request Briefing <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  </Layout>
);

export default StrategyBriefings;
